/** TanStack Query mutation hooks for lesson editing: step CRUD, reorder, publish. */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { lessonKeys } from '@/shared/api/hooks/useLessons';
import { courseKeys } from '@/shared/api/hooks/useCourses';
import type { LessonWithSteps } from '@/shared/api/hooks/useLessons';
import { extractErrorMessage } from '@/shared/utils/extractErrorMessage';
import { toast } from 'sonner';
import type {
  UpdateLessonInput,
  CreateStepInLessonInput,
  UpdateStepInLessonInput,
  ReorderStepsInput,
  Lesson,
  Step,
} from '@formency/shared';

// ─── Optimistic update helpers ───────────────────────────────────────────────

type Snapshot = { prev: LessonWithSteps | undefined };

/**
 * Build the exact query key for a lesson-with-steps query.
 * When courseVersionId is provided, the key is extended so draft vs non-draft
 * queries are cached separately. setQueryData requires an exact key match,
 * unlike invalidateQueries which prefix-matches.
 */
function getQueryKey(lessonId: string, courseVersionId?: string) {
  return [...lessonKeys.withSteps(lessonId), { courseVersionId }];
}

async function snapshotAndCancel(
  qc: ReturnType<typeof useQueryClient>,
  lessonId: string,
  courseVersionId?: string
): Promise<Snapshot> {
  const key = getQueryKey(lessonId, courseVersionId);
  await qc.cancelQueries({ queryKey: key });
  return { prev: qc.getQueryData<LessonWithSteps>(key) };
}

function rollback(
  qc: ReturnType<typeof useQueryClient>,
  lessonId: string,
  context: Snapshot | undefined,
  courseVersionId?: string
) {
  if (context?.prev) {
    qc.setQueryData(getQueryKey(lessonId, courseVersionId), context.prev);
  }
}

function settle(
  qc: ReturnType<typeof useQueryClient>,
  lessonId: string,
  courseVersionId?: string,
  courseId?: string
) {
  qc.invalidateQueries({ queryKey: getQueryKey(lessonId, courseVersionId) });
  // Also refresh the course content tree (e.g. "Not live" labels) when editing within a course
  if (courseId) {
    qc.invalidateQueries({ queryKey: courseKeys.content(courseId) });
  }
}

// ─── Mutations ───────────────────────────────────────────────────────────────

/** Update lesson metadata (title, description, isLive, passPct). */
export function useUpdateLesson(lessonId: string, courseVersionId?: string, courseId?: string) {
  const qc = useQueryClient();
  const queryKey = getQueryKey(lessonId, courseVersionId);
  return useMutation({
    mutationFn: (data: UpdateLessonInput) =>
      api.put<Lesson>(`/lessons/${lessonId}`, {
        ...data,
        ...(courseVersionId && { courseVersionId }),
      }),
    onMutate: async (data) => {
      const snapshot = await snapshotAndCancel(qc, lessonId, courseVersionId);
      qc.setQueryData<LessonWithSteps>(queryKey, (old) => {
        if (!old) return old;
        const merged = { ...old } as Record<string, unknown>;
        for (const [key, val] of Object.entries(data)) {
          if (val !== undefined) merged[key] = val;
        }
        return merged as unknown as LessonWithSteps;
      });
      return snapshot;
    },
    onError: (error: unknown, _vars, context) => {
      rollback(qc, lessonId, context, courseVersionId);
      toast.error(extractErrorMessage(error, 'Failed to update lesson'));
    },
    onSettled: () => settle(qc, lessonId, courseVersionId, courseId),
  });
}

/** Create a new step within a lesson's draft. */
export function useCreateStep(lessonId: string, courseVersionId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateStepInLessonInput) =>
      api.post<Step>(`/lessons/${lessonId}/steps`, {
        ...data,
        ...(courseVersionId && { courseVersionId }),
      }),
    onSuccess: () => settle(qc, lessonId, courseVersionId),
    onError: (error: unknown) => toast.error(extractErrorMessage(error, 'Failed to create step')),
  });
}

/** Update a step within a lesson's draft. Optimistically merges changes into cache. */
export function useUpdateStep(lessonId: string, courseVersionId?: string, courseId?: string) {
  const qc = useQueryClient();
  const queryKey = getQueryKey(lessonId, courseVersionId);
  return useMutation({
    mutationFn: ({ stepId, ...data }: { stepId: string } & UpdateStepInLessonInput) =>
      api.patch<Step>(`/lessons/${lessonId}/steps/${stepId}`, {
        ...data,
        ...(courseVersionId && { courseVersionId }),
      }),
    onMutate: async ({ stepId, ...data }) => {
      const snapshot = await snapshotAndCancel(qc, lessonId, courseVersionId);
      qc.setQueryData<LessonWithSteps>(queryKey, (old) => {
        if (!old) return old;
        // Merge only defined fields to avoid overwriting typed Step fields with `unknown`
        const updateSteps = (steps: Step[]) =>
          steps.map((s) => {
            if (s.id !== stepId) return s;
            const merged = { ...s } as Record<string, unknown>;
            for (const [key, val] of Object.entries(data)) {
              if (val !== undefined) merged[key] = val;
            }
            return merged as Step;
          });
        return {
          ...old,
          learnSteps: updateSteps(old.learnSteps),
          reviseSteps: updateSteps(old.reviseSteps),
        };
      });
      return snapshot;
    },
    onError: (error: unknown, _vars, context) => {
      rollback(qc, lessonId, context, courseVersionId);
      toast.error(extractErrorMessage(error, 'Failed to update step'));
    },
    onSettled: () => settle(qc, lessonId, courseVersionId, courseId),
  });
}

/** Delete a step from a lesson's draft. Optimistically removes from cache. */
export function useDeleteStep(lessonId: string, courseVersionId?: string) {
  const qc = useQueryClient();
  const queryKey = getQueryKey(lessonId, courseVersionId);
  return useMutation({
    mutationFn: ({ stepId }: { stepId: string }) =>
      api.del(`/lessons/${lessonId}/steps/${stepId}`, {
        ...(courseVersionId && { params: { courseVersionId } }),
      }),
    onMutate: async ({ stepId }) => {
      const snapshot = await snapshotAndCancel(qc, lessonId, courseVersionId);
      qc.setQueryData<LessonWithSteps>(queryKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          learnSteps: old.learnSteps.filter((s) => s.id !== stepId),
          reviseSteps: old.reviseSteps.filter((s) => s.id !== stepId),
        };
      });
      return snapshot;
    },
    onError: (error: unknown, _vars, context) => {
      rollback(qc, lessonId, context, courseVersionId);
      toast.error(extractErrorMessage(error, 'Failed to delete step'));
    },
    onSettled: () => settle(qc, lessonId, courseVersionId),
  });
}

/** Reorder steps within a lesson's draft. Optimistically reorders in cache. */
export function useReorderSteps(lessonId: string, courseVersionId?: string) {
  const qc = useQueryClient();
  const queryKey = getQueryKey(lessonId, courseVersionId);
  return useMutation({
    mutationFn: (data: ReorderStepsInput) =>
      api.put(`/lessons/${lessonId}/steps/reorder`, {
        ...data,
        ...(courseVersionId && { courseVersionId }),
      }),
    onMutate: async ({ steps: orderedSteps }) => {
      const snapshot = await snapshotAndCancel(qc, lessonId, courseVersionId);
      qc.setQueryData<LessonWithSteps>(queryKey, (old) => {
        if (!old) return old;
        // Build a lookup: stepId -> { position, category } from the reorder payload
        const orderMap = new Map(
          orderedSteps.map((s) => [s.stepId, { position: s.position, category: s.category }])
        );
        // Collect all steps, reassign category, then split and sort by position
        const allSteps = [...old.learnSteps, ...old.reviseSteps];
        const stepMap = new Map(allSteps.map((s) => [s.id, s]));
        const learn: { step: Step; pos: number }[] = [];
        const revise: { step: Step; pos: number }[] = [];
        for (const [stepId, order] of orderMap) {
          const step = stepMap.get(stepId);
          if (!step) continue;
          const updated = { ...step, category: order.category } as Step;
          if (order.category === 'learn') {
            learn.push({ step: updated, pos: order.position });
          } else {
            revise.push({ step: updated, pos: order.position });
          }
        }
        learn.sort((a, b) => a.pos - b.pos);
        revise.sort((a, b) => a.pos - b.pos);
        return {
          ...old,
          learnSteps: learn.map((e) => e.step),
          reviseSteps: revise.map((e) => e.step),
        };
      });
      return snapshot;
    },
    onError: (error: unknown, _vars, context) => {
      rollback(qc, lessonId, context, courseVersionId);
      toast.error(extractErrorMessage(error, 'Failed to reorder steps'));
    },
    onSettled: () => settle(qc, lessonId, courseVersionId),
  });
}
